import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { companyInfo, getPhoneLink, getEmailLink } from "@/lib/config/company";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Logo } from "@/components/Logo";
import { useTranslation } from "react-i18next";
import { SecurityPaymentBanner } from "@/components/SecurityPaymentBanner";
import { CityLinksBlock } from "@/components/seo/CityLinksBlock";
import { cn } from "@/lib/utils";

const Footer = () => {
  const { t } = useTranslation();
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, margin: "-100px" });

  // Variants d'animation pour les sections
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94], // ease-out-cubic
      },
    },
  };

  // Variants pour les liens de navigation
  const linkVariants = {
    rest: { x: 0, opacity: 0.8 },
    hover: {
      x: 3,
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
  };

  // Variants pour les icônes sociales
  const socialIconVariants = {
    rest: { scale: 1, rotate: 0 },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: { duration: 0.2, ease: "easeOut" },
    },
  };

  // Navigation links data
  const navigationLinks = [
    {
      title: t('footer.about'),
      links: [
        { to: "/about", label: t('footer.whoWeAre') },
        { to: "/how-it-works", label: t('footer.howItWorks') },
        { to: "/blog", label: t('footer.blog') },
      ],
    },
    {
      title: t('footer.rental'),
      links: [
        { to: "/rent", label: t('footer.rentCar') },

        { to: "/help", label: t('footer.help') },
      ],
    },
    {
      title: t('footer.agencies'),
      links: [
        { to: "/agences", label: t('footer.agencyPartnership') },
        { to: "/location-voiture/casablanca", label: t('footer.exploreCasablanca') },
        { to: "/guide", label: t('footer.guide') },
      ],
    },
    {
      title: t('footer.support'),
      links: [
        { to: "/contact", label: t('footer.contact') },
        { to: "/faq", label: t('footer.faq') },
        { to: "/emergency", label: t('footer.emergency') },
      ],
    },
  ];

  const similarSearchesLinks = [
    { to: "/location-voiture-150-dh", label: t('footer.similarSearchCar150') },
    { to: "/location-voiture-casablanca-pas-cher", label: t('footer.similarSearchCasablancaCheap') },
    { to: "/location-voiture-aeroport-casablanca", label: t('footer.similarSearchAirportCasablanca') },
    { to: "/location-voiture-longue-duree-maroc", label: t('footer.similarSearchLongTerm') },
  ];

  const similarSearchesSection = {
    title: t('footer.similarSearches'),
    links: similarSearchesLinks,
  };

  const cityLinksSection = {
    title: t('footer.rentalByCity'),
    component: <CityLinksBlock variant="footer" title={t('footer.rentalByCity')} />,
  };

  const legalLinks = [
    { to: "/legal/privacy", label: t('footer.privacy') },
    { to: "/legal/terms", label: t('footer.terms') },
    { to: "/legal/cancellation-policy", label: t('footer.cancellation') },
    { to: "/legal", label: t('footer.legal') },
  ];

  // Composant pour le nouveau logo X
  const XLogo = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );

  const socialLinks = [
    {
      href: companyInfo.social.facebook,
      icon: Facebook,
      label: "Facebook",
      ariaLabel: t('footer.socialAriaLabel', { platform: "Facebook" }),
    },
    {
      href: companyInfo.social.twitter,
      icon: XLogo,
      label: "X",
      ariaLabel: t('footer.socialAriaLabel', { platform: "X" }),
    },
    {
      href: companyInfo.social.instagram,
      icon: Instagram,
      label: "Instagram",
      ariaLabel: t('footer.socialAriaLabel', { platform: "Instagram" }),
    },
    {
      href: companyInfo.social.linkedin,
      icon: Linkedin,
      label: "LinkedIn",
      ariaLabel: t('footer.socialAriaLabel', { platform: "LinkedIn" }),
    },
  ];

  const BookOnWebLink = ({ className }: { className?: string }) => (
    <div className={cn("flex flex-col gap-2", className)}>
      <Link
        to="/search?focus=location"
        aria-label={t("footer.bookOnWebAria")}
        className="inline-flex items-center justify-center rounded-lg border border-[#10b981]/40 bg-[#10b981]/10 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#10b981]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10b981] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1A1A]"
      >
        {t("footer.bookOnWebCta")}
      </Link>
    </div>
  );

  const contactInner = (
    <>
      <h4 className="font-bold text-xs sm:text-sm uppercase tracking-[0.1em] mb-4 sm:mb-5 text-[#10b981]">
        {t("footer.contactUs")}
      </h4>
      <div className="space-y-3">
        <motion.a
          href={getEmailLink()}
          whileHover={{ x: 3, opacity: 1 }}
          className="flex items-center gap-3 text-sm sm:text-base text-white hover:text-[#10b981] font-medium transition-colors duration-200 group"
          aria-label={t("footer.sendEmailAria", { email: companyInfo.email })}
        >
          <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:text-[#10b981] transition-colors duration-200" />
          <span>{companyInfo.email}</span>
        </motion.a>
        <motion.a
          href={getPhoneLink()}
          whileHover={{ x: 3, opacity: 1 }}
          className="flex items-center gap-3 text-sm sm:text-base text-white hover:text-[#10b981] font-medium transition-colors duration-200 group"
          aria-label={t("footer.callAria", { phone: companyInfo.phoneDisplay })}
        >
          <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:text-[#10b981] transition-colors duration-200" />
          <span>{companyInfo.phoneDisplay}</span>
        </motion.a>
        <div className="flex items-center gap-3 text-sm sm:text-base text-white font-medium">
          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          <span>
            {companyInfo.address.city}, {companyInfo.address.country}
          </span>
        </div>
      </div>
    </>
  );

  const socialSectionInner = (
    <>
      <h4 className="font-bold text-xs sm:text-sm uppercase tracking-[0.1em] mb-4 sm:mb-5 text-[#10b981]">
        {t("footer.followUs")}
      </h4>
      <div className="flex gap-4 sm:gap-6">
        {socialLinks.map((social) => {
          const Icon = social.icon;
          const isXLogo = social.label === "X";
          return (
            <motion.a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              variants={socialIconVariants}
              initial="rest"
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2D2D2D] text-white transition-colors duration-200 hover:bg-[#10b981] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#10b981] focus:ring-offset-2 focus:ring-offset-[#1A1A1A] sm:h-12 sm:w-12"
              aria-label={social.ariaLabel}
            >
              {isXLogo ? <XLogo className="h-5 w-5 sm:h-6 sm:w-6" /> : <Icon className="h-5 w-5 sm:h-6 sm:w-6" />}
            </motion.a>
          );
        })}
      </div>
    </>
  );

  return (
    <footer ref={footerRef} className="relative bg-[#1A1A1A] text-white mt-0 overflow-hidden">
      {/* Gradient overlay subtil pour profondeur */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] via-[#1A1A1A] to-[#0F0F0F] opacity-50 pointer-events-none" />

      <div className="relative container mx-auto px-6 sm:px-8 lg:px-12 max-w-[1280px]">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="py-8 sm:py-10 lg:py-12"
        >
          {/* NAVIGATION PRINCIPALE */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-6 sm:gap-8 lg:gap-10 mb-8 sm:mb-10"
          >
            {navigationLinks.map((section, index) => (
              <motion.nav
                key={section.title}
                variants={itemVariants}
                aria-label={`Navigation ${section.title}`}
                className="flex flex-col"
              >
                <h3 className="font-bold text-xs sm:text-sm uppercase tracking-[0.1em] mb-4 sm:mb-5 text-[#10b981]">
                  {section.title}
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {section.links.map((link) => (
                    <li key={link.to}>
                      <motion.div
                        variants={linkVariants}
                        initial="rest"
                        whileHover="hover"
                        className="inline-block"
                      >
                        <Link
                          to={link.to}
                          className="text-sm sm:text-base text-white hover:text-[#10b981] font-medium transition-colors duration-200 inline-flex items-center gap-2 group"
                        >
                          <span className="text-white">{link.label}</span>
                          <motion.span
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            initial={{ x: -4 }}
                            whileHover={{ x: 0 }}
                          >
                            →
                          </motion.span>
                        </Link>
                      </motion.div>
                    </li>
                  ))}
                </ul>
              </motion.nav>
            ))}
            <motion.div variants={itemVariants} className="flex flex-col">
              {cityLinksSection.component}
            </motion.div>
            <motion.nav variants={itemVariants} aria-label={t("footer.similarSearches")} className="flex flex-col">
              <h3 className="font-bold text-xs sm:text-sm uppercase tracking-[0.1em] mb-4 sm:mb-5 text-[#10b981]">
                {t("footer.similarSearches")}
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {similarSearchesLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm sm:text-base text-white hover:text-[#10b981] font-medium transition-colors duration-200 inline-flex items-center gap-2"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.nav>
          </motion.div>

          {/* CONTACT & SOCIAL */}
          <motion.div
            variants={itemVariants}
            className="border-t border-[#2D2D2D] pt-8 sm:pt-10 mb-8 sm:mb-10"
          >
            <div className="flex flex-col gap-8 sm:gap-10 lg:hidden">
              <div className="flex flex-row items-start gap-4 sm:gap-6">
                <motion.address variants={itemVariants} className="not-italic min-w-0 flex-1">
                  {contactInner}
                </motion.address>
                <motion.div variants={itemVariants} className="flex min-w-0 shrink-0 flex-col items-end">
                  <h4 className="mb-3 max-w-[7.5rem] text-right text-xs font-bold uppercase leading-snug tracking-[0.1em] text-[#10b981] sm:mb-4 sm:max-w-none sm:text-sm">
                    {t("footer.downloadApp")}
                  </h4>
                  <BookOnWebLink className="items-end" />
                </motion.div>
              </div>
              <motion.div variants={itemVariants}>{socialSectionInner}</motion.div>
            </div>

            <div className="hidden gap-8 lg:grid lg:grid-cols-3 xl:gap-10">
              <motion.address variants={itemVariants} className="not-italic">
                {contactInner}
              </motion.address>
              <motion.div variants={itemVariants}>{socialSectionInner}</motion.div>
              <motion.div variants={itemVariants} className="flex flex-col items-start lg:items-center">
                <h4 className="mb-4 w-full font-bold text-xs uppercase tracking-[0.1em] text-[#10b981] sm:mb-5 sm:text-sm lg:text-center">
                  {t("footer.downloadApp")}
                </h4>
                <BookOnWebLink className="items-start lg:items-center" />
              </motion.div>
            </div>
          </motion.div>

          {/* SECURITY & PAYMENT BANNER */}
          <motion.div variants={itemVariants} className="border-t border-[#2D2D2D] pt-6 sm:pt-8 pb-4 sm:pb-6">
            <SecurityPaymentBanner variant="footer" />
          </motion.div>

          {/* LEGAL & COPYRIGHT */}
          <motion.div variants={itemVariants} className="border-t border-[#2D2D2D] pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6">
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <Logo size="sm" color="text-white" />
                <p className="text-xs sm:text-sm text-white text-center sm:text-left font-medium">
                  &copy; {new Date().getFullYear()} {companyInfo.name}. {t('footer.allRightsReserved')}
                </p>
              </div>
              <nav
                className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6 text-xs sm:text-sm"
                aria-label={t('footer.legalNavAria')}
              >
                {legalLinks.map((link) => (
                  <motion.div
                    key={link.to}
                    whileHover={{ x: 2, opacity: 1 }}
                    initial={{ opacity: 0.8 }}
                  >
                    <Link
                      to={link.to}
                      className="text-white hover:text-[#10b981] font-medium transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
